// Turns an arbitrary OCR result object into a human-friendly display model.
//
// The renderer is deliberately DATA-DRIVEN rather than one hard-coded layout
// per document type: it inspects the *shape* of each value and decides how to
// present it. That means it works for every built-in preset (invoice, bill,
// receipt, purchase order, business card, ID), for `generic` mode, AND for any
// caller-defined custom schema — with no per-type code.
//
//   scalar (name, total, date …)      -> a key/value entry
//   scalar whose key looks monetary   -> grouped into the Totals card
//   array of objects (line items …)   -> a table
//   array of scalars (tags …)         -> a chip list
//   nested object (vendor address …)  -> its own sub-section
//   generic { fields[], tables[] }     -> flattened into details + tables

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type JsonObject = { [key: string]: JsonValue };

export interface Entry {
  label: string;
  value: string;
  empty: boolean;
  money: boolean;
  link?: 'url' | 'email';
}

export interface TableColumn {
  key: string;
  label: string;
  money: boolean;
}

export interface DetailsSection {
  kind: 'details';
  title: string;
  entries: Entry[];
}

export interface TableSection {
  kind: 'table';
  title: string;
  columns: TableColumn[];
  rows: Record<string, string>[];
}

export interface ListSection {
  kind: 'list';
  title: string;
  items: string[];
}

export type Section = DetailsSection | TableSection | ListSection;

export interface TotalRow {
  label: string;
  value: string;
  primary: boolean;
}

export interface DisplayModel {
  title: string;
  subtitle: string;
  currency?: string;
  totals: TotalRow[];
  sections: Section[];
}

// --- small helpers -------------------------------------------------------

const ACRONYMS: Record<string, string> = {
  id: 'ID',
  po: 'PO',
  sku: 'SKU',
  vat: 'VAT',
  url: 'URL',
  gstin: 'GSTIN',
  abn: 'ABN',
  ic: 'IČ',
  dic: 'DIČ',
  iban: 'IBAN',
  bic: 'BIC',
};

/** camelCase / snake_case / kebab-case -> "Title Case", respecting acronyms. */
export function humanize(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((w) => {
      const lower = w.toLowerCase();
      if (ACRONYMS[lower]) return ACRONYMS[lower];
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

const norm = (k: string) => k.toLowerCase().replace(/[^a-z0-9]/g, '');

// Keys grouped into the highlighted Totals card.
const TOTAL_KEYS = new Set([
  'total',
  'grandtotal',
  'nettotal',
  'subtotal',
  'tax',
  'taxtotal',
  'vat',
  'amountdue',
  'amountpaid',
  'balancedue',
  'balance',
  'discount',
  'discounttotal',
  'shipping',
  'shippingtotal',
  'tip',
  'adjustment',
]);
// The single figure to emphasise, in priority order.
const PRIMARY_TOTAL_KEYS = ['total', 'grandtotal', 'amountdue', 'balancedue'];

// Column/field keys rendered right-aligned as money.
const MONEY_KEYS = new Set([
  'amount',
  'rate',
  'price',
  'unitprice',
  'linetotal',
  'total',
  'subtotal',
  'grandtotal',
  'tax',
  'taxtotal',
  'taxamount',
  'amountdue',
  'amountpaid',
  'balancedue',
  'discount',
  'discounttotal',
  'shipping',
  'shippingtotal',
  'tip',
  'adjustment',
]);

const isTotalKey = (k: string) => TOTAL_KEYS.has(norm(k));
const isMoneyKey = (k: string) => MONEY_KEYS.has(norm(k));

// Canonical ordering for line-item table columns. The model returns object
// keys in an arbitrary order (a bill once came back with `amount` first), but
// the invoice convention is: what → how many → unit price → tax → line total.
// So description-like columns are pinned to the front and running-total columns
// to the back; everything else keeps the order it was first seen in.
const COL_FIRST = new Set([
  'description',
  'desc',
  'item',
  'itemname',
  'name',
  'product',
  'service',
  'details',
  'particulars',
  'lineitem',
  // item identifiers sit next to the description, not out among the numbers
  'sku',
  'code',
  'itemcode',
  'productcode',
  'hsn',
  'hsnsac',
]);
const COL_LAST = new Set([
  'amount',
  'lineamount',
  'linetotal',
  'total',
  'subtotal',
  'grandtotal',
  'nettotal',
  'netamount',
  'grossamount',
]);

function columnWeight(key: string, firstSeenIndex: number): number {
  const n = norm(key);
  if (COL_FIRST.has(n)) return firstSeenIndex; // front, keep relative order
  if (COL_LAST.has(n)) return 1000 + firstSeenIndex; // back, keep relative order
  return 100 + firstSeenIndex; // middle (qty, rate, tax, custom fields)
}

function isPlainObject(v: JsonValue): v is JsonObject {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isEmptyValue(v: JsonValue): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (isPlainObject(v)) return Object.keys(v).length === 0;
  return false;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO dates -> "1 Jun 2026"; everything else is returned unchanged. */
function formatValue(key: string, v: JsonValue): string {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  if (/date/i.test(key) && /^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-');
    const mi = Number(m) - 1;
    if (mi >= 0 && mi < 12) return `${Number(d)} ${MONTHS[mi]} ${y}`;
  }
  return s;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function linkKind(key: string, value: string): Entry['link'] {
  if (/e-?mail/i.test(key) || EMAIL_RE.test(value)) return EMAIL_RE.test(value) ? 'email' : undefined;
  if (/website|url|link/i.test(key) || /^https?:\/\//i.test(value)) return 'url';
  return undefined;
}

function toEntry(key: string, v: JsonValue): Entry {
  const raw = formatValue(key, v);
  const empty = raw === '';
  return {
    label: humanize(key),
    value: empty ? '—' : raw,
    empty,
    money: isMoneyKey(key),
    link: empty ? undefined : linkKind(key, raw),
  };
}

// Flatten a nested object one level deep into "Parent · Child" entries so an
// address block reads cleanly instead of dumping JSON.
function objectEntries(prefix: string, obj: JsonObject, includeEmpty: boolean): Entry[] {
  const out: Entry[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (isPlainObject(v)) {
      out.push(...objectEntries(k, v, includeEmpty));
    } else if (Array.isArray(v)) {
      if (!includeEmpty && isEmptyValue(v)) continue;
      out.push({
        label: humanize(k),
        value: v.map((x) => String(x)).join(', ') || '—',
        empty: isEmptyValue(v),
        money: false,
      });
    } else {
      if (!includeEmpty && isEmptyValue(v)) continue;
      out.push(toEntry(k, v));
    }
  }
  return out;
}

function isArrayOfObjects(v: JsonValue): v is JsonObject[] {
  return Array.isArray(v) && v.length > 0 && v.every((x) => isPlainObject(x));
}

function isArrayOfScalars(v: JsonValue): v is (string | number | boolean)[] {
  return Array.isArray(v) && v.every((x) => x === null || typeof x !== 'object');
}

/** Build a table from an array of objects, columns = union of keys (in order). */
function tableFromArray(title: string, rowsIn: JsonObject[]): TableSection {
  const colKeys: string[] = [];
  for (const row of rowsIn) {
    for (const k of Object.keys(row)) if (!colKeys.includes(k)) colKeys.push(k);
  }
  // Reorder into the conventional invoice column layout (see columnWeight).
  const ordered = colKeys
    .map((key, idx) => ({ key, weight: columnWeight(key, idx) }))
    .sort((a, b) => a.weight - b.weight)
    .map((c) => c.key);
  const columns: TableColumn[] = ordered.map((k) => ({
    key: k,
    label: humanize(k),
    money: isMoneyKey(k),
  }));
  const rows = rowsIn.map((row) => {
    const r: Record<string, string> = {};
    for (const k of colKeys) {
      const cell = row[k];
      if (cell === undefined || cell === null) r[k] = '';
      else if (typeof cell === 'object') r[k] = JSON.stringify(cell);
      else r[k] = formatValue(k, cell);
    }
    return r;
  });
  return { kind: 'table', title, columns, rows };
}

// generic-mode { headers:[], rows:[[]] } table.
function isGenericTable(v: JsonValue): v is JsonObject {
  return isPlainObject(v) && Array.isArray((v as JsonObject).rows);
}

function tableFromGeneric(t: JsonObject): TableSection {
  const headers = Array.isArray(t.headers) ? (t.headers as JsonValue[]).map((h) => String(h)) : [];
  const rowsIn = Array.isArray(t.rows) ? (t.rows as JsonValue[]) : [];
  const widestRow = rowsIn.reduce<number>(
    (m, r) => Math.max(m, Array.isArray(r) ? r.length : 0),
    0
  );
  const colCount: number = headers.length || widestRow;
  const columns: TableColumn[] = [];
  for (let i = 0; i < colCount; i += 1) {
    const label = headers[i] ?? `Col ${i + 1}`;
    columns.push({ key: String(i), label, money: isMoneyKey(label) });
  }
  const rows = rowsIn.map((r) => {
    const arr = Array.isArray(r) ? r : [r];
    const obj: Record<string, string> = {};
    for (let i = 0; i < colCount; i += 1) obj[String(i)] = arr[i] == null ? '' : String(arr[i]);
    return obj;
  });
  const title = typeof t.title === 'string' && t.title ? t.title : 'Table';
  return { kind: 'table', title, columns, rows };
}

// --- the main builder ----------------------------------------------------

function pickString(obj: JsonObject, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (isPlainObject(v) && typeof v.name === 'string' && v.name.trim()) return v.name.trim();
  }
  return undefined;
}

export function buildDisplayModel(
  data: unknown,
  docTypeLabel: string,
  opts: { includeEmpty?: boolean } = {}
): DisplayModel {
  const includeEmpty = !!opts.includeEmpty;

  // Non-object payloads (rare) — show as a single detail.
  if (!isPlainObject(data as JsonValue)) {
    return {
      title: docTypeLabel || 'Result',
      subtitle: '',
      totals: [],
      sections: [
        {
          kind: 'details',
          title: 'Result',
          entries: [{ label: 'Value', value: String(data), empty: false, money: false }],
        },
      ],
    };
  }

  const obj = data as JsonObject;
  const detailEntries: Entry[] = [];
  const objectSections: DetailsSection[] = [];
  const tableSections: TableSection[] = [];
  const listSections: ListSection[] = [];
  const totals: TotalRow[] = [];

  const currency =
    typeof obj.currency === 'string' && obj.currency.trim() ? obj.currency.trim() : undefined;

  for (const [key, value] of Object.entries(obj)) {
    // generic mode: { fields: [{key,value}], tables: [{title,headers,rows}] }
    if (key === 'fields' && isArrayOfObjects(value)) {
      for (const f of value) {
        const k = typeof f.key === 'string' ? f.key : '';
        const v = (f.value ?? '') as JsonValue;
        if (!k) continue;
        if (!includeEmpty && isEmptyValue(v)) continue;
        detailEntries.push(toEntry(k, v));
      }
      continue;
    }
    if (key === 'tables' && Array.isArray(value)) {
      for (const t of value) if (isGenericTable(t)) tableSections.push(tableFromGeneric(t));
      continue;
    }

    if (isMoneyScalar(key, value)) {
      totals.push({
        label: humanize(key),
        value: formatValue(key, value),
        primary: PRIMARY_TOTAL_KEYS.includes(norm(key)),
      });
      continue;
    }

    const empty = isEmptyValue(value);
    if (empty && !includeEmpty) continue;

    if (typeof value !== 'object' || value === null) {
      detailEntries.push(toEntry(key, value));
    } else if (isArrayOfObjects(value)) {
      tableSections.push(tableFromArray(humanize(key), value));
    } else if (isArrayOfScalars(value)) {
      if (value.length) {
        listSections.push({ kind: 'list', title: humanize(key), items: value.map((x) => String(x)) });
      } else if (includeEmpty) {
        detailEntries.push({ label: humanize(key), value: '—', empty: true, money: false });
      }
    } else if (isPlainObject(value)) {
      const entries = objectEntries(key, value, includeEmpty);
      if (entries.length) objectSections.push({ kind: 'details', title: humanize(key), entries });
    }
  }

  // Only one primary is emphasised — keep the highest-priority one.
  let primaryTaken = false;
  for (const key of PRIMARY_TOTAL_KEYS) {
    const row = totals.find((t) => norm(t.label.replace(/\s/g, '')) === key || norm(t.label) === key);
    if (row && !primaryTaken) {
      row.primary = true;
      primaryTaken = true;
    } else if (row) {
      row.primary = false;
    }
  }
  if (!primaryTaken && totals.length) {
    totals.forEach((t) => (t.primary = false));
    totals[totals.length - 1].primary = true;
  }

  const sections: Section[] = [];
  if (detailEntries.length) sections.push({ kind: 'details', title: 'Details', entries: detailEntries });
  sections.push(...objectSections, ...tableSections, ...listSections);

  const party = pickString(obj, [
    'vendorName',
    'merchantName',
    'company',
    'fullName',
    'vendor',
    'buyer',
    'name',
  ]);
  const primaryId = pickString(obj, [
    'invoiceNumber',
    'billNumber',
    'receiptNumber',
    'poNumber',
    'documentNumber',
    'orderNumber',
  ]);
  const docTypeName =
    typeof obj.documentType === 'string' && obj.documentType.trim()
      ? obj.documentType.trim()
      : docTypeLabel;

  const title = party || primaryId || docTypeName || 'Extracted data';
  const subtitleBits: string[] = [];
  if (docTypeName && docTypeName !== title) subtitleBits.push(docTypeName);
  if (primaryId && primaryId !== title) subtitleBits.push(`#${primaryId}`);

  return { title, subtitle: subtitleBits.join(' · '), currency, totals, sections };
}

function isMoneyScalar(key: string, value: JsonValue): boolean {
  return (
    isTotalKey(key) &&
    (typeof value === 'string' || typeof value === 'number') &&
    String(value).trim() !== ''
  );
}
